import React from 'react';
import { Clock, CalendarDays, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../App';
import { Seo } from '../../components/seo/Seo';
import { PublicLayout } from '../../components/public/PublicLayout';
import { Container, CtaBand } from '../../components/public/PublicSections';
import { ContentBlocks, TableOfContents, RelatedLinks } from '../../components/public/ContentBlocks';
import { ArticleCard } from '../../components/public/ArticleCard';
import { articlesForGuideAnchor, TIPS_COPY } from '../../seo/articles';
import { GUIDE, GUIDE_META, guideReadingMinutes } from '../../seo/guideContent';
import { LAYOUT_COPY } from '../../seo/publicContent';
import { pageMeta, pagePath, alternatesFor } from '../../seo/seoConfig.mjs';
import { breadcrumbSchema, articleSchema } from '../../seo/structuredData';

const formatDate = (iso, lang) => new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' });

export default function PublicGuidePage({ lang }) {
  const { user } = useAuth();
  const g = GUIDE[lang];
  const meta = pageMeta('guide', lang);
  const nav = LAYOUT_COPY[lang].nav;
  const appHref = user ? (user.onboarding_complete ? '/dashboard' : '/onboarding') : '/auth';
  const minutes = guideReadingMinutes(lang);
  const related = [
    { key: 'tips', to: pagePath('tips', lang), label: nav.tips },
    { key: 'features', to: pagePath('features', lang), label: nav.features },
    { key: 'faq', to: pagePath('faq', lang), label: nav.faq },
    { key: 'home', to: pagePath('home', lang), label: nav.home },
  ];

  return (
    <PublicLayout lang={lang} pageKey="guide">
      <Seo
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        path={pagePath('guide', lang)}
        lang={lang}
        alternates={alternatesFor('guide')}
        ogType="article"
        jsonLd={[
          breadcrumbSchema([
            { name: nav.home, path: pagePath('home', lang) },
            { name: nav.guide, path: pagePath('guide', lang) },
          ]),
          articleSchema({ lang, headline: g.h1, description: meta.description, path: pagePath('guide', lang), keywords: meta.keywords, wordCountMinutes: minutes }),
        ]}
      />

      <article>
        <header className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(600px circle at 15% 0%, rgba(243,179,61,0.12), transparent 55%)' }} />
          <Container className="relative pt-14 pb-10 sm:pt-20 sm:pb-14 max-w-4xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--lux-gold)] mb-3">{g.eyebrow}</p>
            <h1 className="font-display text-4xl sm:text-5xl leading-[1.08] text-white" data-testid="guide-title">{g.h1}</h1>
            <p className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed" data-testid="guide-intro">{g.intro}</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/45" data-testid="guide-meta">
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[var(--lux-gold)]" /> {minutes} {g.readingTimeLabel}</span>
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[var(--lux-gold)]" /> {g.updatedLabel} {formatDate(GUIDE_META.dateModified, lang)}</span>
              <span>{g.authorLabel}</span>
            </div>
          </Container>
        </header>

        <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-8">
          <aside className="lg:col-span-4 order-first lg:order-last">
            <div className="lg:sticky lg:top-24 space-y-4">
              <TableOfContents title={g.tocTitle} sections={g.sections} testId="guide-toc" />
              <RelatedLinks title={g.relatedTitle} links={related} testId="guide-related" />
            </div>
          </aside>
          <div className="lg:col-span-8 max-w-3xl" data-testid="guide-body">
            {g.sections.map(section => (
              <section key={section.id} id={section.id} className="scroll-mt-24 pt-10 first:pt-0" aria-labelledby={`${section.id}-title`} data-testid={`guide-section-${section.id}`}>
                <h2 id={`${section.id}-title`} className="font-display text-2xl sm:text-3xl text-white">{section.title}</h2>
                <ContentBlocks blocks={section.blocks} testIdPrefix={`guide-${section.id}`} />
                {articlesForGuideAnchor(section.id, lang).length > 0 && (
                  <div className="mt-6 rounded-2xl border border-white/10 bg-black/20 p-4 sm:p-5" data-testid={`guide-deepen-${section.id}`}>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--lux-gold)] mb-4">{TIPS_COPY[lang].deepen}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {articlesForGuideAnchor(section.id, lang).map(a => <ArticleCard key={a.key} article={a} lang={lang} compact />)}
                    </div>
                  </div>
                )}
              </section>
            ))}
            <aside className="mt-12 rounded-2xl border border-white/10 bg-lux-panel/90 p-5 flex gap-4" data-testid="guide-disclaimer">
              <AlertTriangle className="w-5 h-5 text-[var(--lux-amber)] shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-semibold text-white">{g.disclaimerTitle}</p>
                <p className="mt-1 text-sm text-white/60 leading-relaxed">{g.disclaimer}</p>
              </div>
            </aside>
          </div>
        </Container>
      </article>

      <CtaBand title={g.ctaTitle} sub={g.ctaText} ctaLabel={g.ctaLabel} to={appHref} testId="guide-final-cta" />
    </PublicLayout>
  );
}
